import {IconAddNew, IconRemove} from '@wandb/weave/components/Icon';
import {Pill} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import numeral from 'numeral';
import React, {useContext} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, type StrictInputProps} from 'semantic-ui-react';

import {type Coupon} from '../../../generated/graphql';
import {
  type PlanWithPrivileges,
  type PrimaryStripePlanInfoWithUnitPrice,
} from '../../../util/accounts/pricing';
import {contactSalesPricing, pricing} from '../../../util/urls';
import {
  formatAsDollarWithCents,
  getCouponDescription,
  getDiscountedMonthlyCostPerSeat,
} from '../AccountSettings/BillingTab/PlanCard/PlanCardComponents';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {TeamsCheckoutModalContext} from './TeamsPlanCheckoutModal';
import * as S from './TeamsPlanTab.styles';

export const PlanSeatPanel = ({
  plan,
  proPlanInsteadOfTeamsPlan,
}: {
  plan: PrimaryStripePlanInfoWithUnitPrice;
  proPlanInsteadOfTeamsPlan: Boolean;
}) => {
  const contextValue = useContext(TeamsCheckoutModalContext);
  if (contextValue == null) {
    return null;
  }
  const {
    seats,
    setSeats,
    yearlyPlan,
    monthlyPlan,
    minSeats,
    isAlreadyTeamsMonthly,
    coupon,
  } = contextValue;
  const yearlyDiscountAmount =
    (seats * (12 * monthlyPlan.unitPrice - yearlyPlan.unitPrice)) / 100;
  return (
    <>
      {isAlreadyTeamsMonthly &&
        (plan.billingInterval === 'year' ? (
          <S.SavingMessageContainer>
            <Icon name="star" className="large" />
            <S.SavingMessage>
              You are saving ${yearlyDiscountAmount} with an annual
              subscription.
            </S.SavingMessage>
          </S.SavingMessageContainer>
        ) : (
          <S.SavingWarningMessageContainer>
            <Icon name="lightbulb outline" className="large" />
            <S.SavingWarningMessage>
              Save ${yearlyDiscountAmount} per year by switching to an annual
              plan.
            </S.SavingWarningMessage>
          </S.SavingWarningMessageContainer>
        ))}
      <S.AddSeatTitle>
        {proPlanInsteadOfTeamsPlan ? 'Add Models Seats' : 'Add Users'}
      </S.AddSeatTitle>
      <SeatControl
        // Temporarily use yearly teams plan to show total cost for base Pro Plan (Weave Tier is Free)
        // Todo (mena-david): Update checkout price to use Stripe Preview invoice feature
        proposedPlan={proPlanInsteadOfTeamsPlan ? yearlyPlan : plan}
        seats={seats}
        minSeats={minSeats}
        setSeats={setSeats}
        coupon={coupon}
      />
    </>
  );
};

export const SeatControl = ({
  proposedPlan,
  seats,
  setSeats,
  minSeats = 1,
  coupon,
}: {
  proposedPlan: PrimaryStripePlanInfoWithUnitPrice;
  seats: number;
  setSeats: (seats: number) => void;
  minSeats?: number;
  coupon?: Coupon | null;
}) => {
  const totalPrice = (proposedPlan.unitPrice * seats) / 100;
  let discountedTotalPrice = null;
  // `coupon` is currently only passed in at the TrialEndPage when purchasing a new yearly Teams plan for the first time.
  // This has not been tested with existing Teams plans when increasing the number of seats - the math may or may not be correct.
  if (coupon != null && proposedPlan.billingInterval === 'year') {
    const discountedMonthlyPricePerSeat = getDiscountedMonthlyCostPerSeat(
      proposedPlan.unitPrice / (12 * 100),
      coupon,
      seats,
      true
    );
    discountedTotalPrice = discountedMonthlyPricePerSeat * seats * 12;
  }
  if (coupon != null && proposedPlan.billingInterval === 'month') {
    const discountedMonthlyPricePerSeat = getDiscountedMonthlyCostPerSeat(
      proposedPlan.unitPrice / 100,
      coupon,
      seats,
      false
    );
    discountedTotalPrice = discountedMonthlyPricePerSeat * seats;
  }
  return (
    <Tailwind>
      <div className="flex flex-col">
        <S.AddSeatBody>
          <S.NumberInputSection>
            <NumberInputWithRange
              min={minSeats}
              max={proposedPlan.maxSeats}
              value={seats}
              setValue={setSeats}
            />
          </S.NumberInputSection>
          <S.AmountSection>
            <S.TotalAmountSection>
              {discountedTotalPrice == null ? (
                <S.TotalAmount>${totalPrice.toLocaleString()}</S.TotalAmount>
              ) : (
                <div className="flex items-end">
                  <div className="mr-[9px] text-2xl text-moon-600 line-through">
                    ${totalPrice.toLocaleString()}
                  </div>
                  <S.TotalAmount>
                    {formatAsDollarWithCents(discountedTotalPrice)}
                  </S.TotalAmount>
                </div>
              )}
              <S.BillingCycle>/{proposedPlan.billingInterval}</S.BillingCycle>
            </S.TotalAmountSection>
          </S.AmountSection>
        </S.AddSeatBody>
        {coupon != null && (
          <div className="mt-1 flex justify-end">
            <Pill label={getCouponDescription(coupon)} color="gold" />
          </div>
        )}
      </div>
      <S.AddSeatMaxWarningWrapper>
        {seats >= proposedPlan.maxSeats ? (
          <S.AddSeatMaxWarning>
            You've reached the maximum number of seats for this plan. To add
            more seats,{' '}
            <S.ContactSalesLink href={contactSalesPricing()}>
              contact sales
            </S.ContactSalesLink>
            .
          </S.AddSeatMaxWarning>
        ) : null}
      </S.AddSeatMaxWarningWrapper>
    </Tailwind>
  );
};

export const StruckPlanSeatControl = ({
  proposedPlan,
  struckPlan,
}: {
  proposedPlan: PrimaryStripePlanInfoWithUnitPrice;
  struckPlan: PrimaryStripePlanInfoWithUnitPrice;
}) => {
  const contextValue = useContext(TeamsCheckoutModalContext);
  if (contextValue == null) {
    return null;
  }
  const {seats, setSeats, minSeats} = contextValue;
  const totalPrice = (proposedPlan.unitPrice * seats) / 100;
  const struckPrice = (struckPlan.unitPrice * seats * 12) / 100;
  return (
    <>
      <S.AddSeatBody>
        <S.NumberInputSection>
          <NumberInputWithRange
            min={minSeats}
            max={proposedPlan.maxSeats}
            value={seats}
            setValue={setSeats}
          />
          <S.StruckPlanAnnotation>
            x {struckPlan.unitPrice / 100}/user x 12 mo
          </S.StruckPlanAnnotation>
        </S.NumberInputSection>
        <S.AmountSection>
          <S.StruckAmountSection>
            <S.StruckAmount>${struckPrice.toLocaleString()}</S.StruckAmount>
            <S.BillingCycle>/{proposedPlan.billingInterval}</S.BillingCycle>
          </S.StruckAmountSection>
          <S.TotalAmountSection>
            <S.TotalAmount>${totalPrice.toLocaleString()}</S.TotalAmount>
            <S.BillingCycle>/{proposedPlan.billingInterval}</S.BillingCycle>
          </S.TotalAmountSection>
        </S.AmountSection>
      </S.AddSeatBody>
      <S.AddSeatMaxWarningWrapper>
        {seats >= proposedPlan.maxSeats ? (
          <S.AddSeatMaxWarning>
            You've reached the maximum number of seats for a teams plan. To add
            more seats,{' '}
            <S.ContactSalesLink href={contactSalesPricing()}>
              contact sales
            </S.ContactSalesLink>
            .
          </S.AddSeatMaxWarning>
        ) : null}
      </S.AddSeatMaxWarningWrapper>
    </>
  );
};

const DecrementButton = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) => {
  return (
    <S.NumberButton
      className="left icon mini attached"
      variant="secondary"
      size="large"
      onClick={onClick}
      disabled={disabled}>
      <IconRemove />
    </S.NumberButton>
  );
};

export const NumberInputWithRange = ({
  min,
  max,
  value,
  setValue,
  showReduceTooltip = false,
}: {
  min: number;
  max: number;
  value: number;
  setValue: (value: number) => void;
  showReduceTooltip?: boolean;
}) => {
  const handleChange = (event => {
    const newValue = parseInt(event.target.value, 10);
    if (!isNaN(newValue) && newValue >= min && newValue <= max) {
      setValue(newValue);
    }
  }) as StrictInputProps['onChange'];

  const handleDecrement = () => {
    if (value > min) {
      setValue(value - 1);
    }
  };

  const handleIncrement = () => {
    if (value < max) {
      setValue(value + 1);
    }
  };

  const decrementButton = (
    <DecrementButton onClick={handleDecrement} disabled={value <= min} />
  );

  return (
    <S.NumberInput
      type="number"
      className="mini compact attached"
      value={value}
      min={min}
      max={max}
      onChange={handleChange}>
      {showReduceTooltip && value <= min ? (
        <Tooltip trigger={decrementButton} position="bottom right">
          <Tailwind>
            <div className="text-12 max-w-[300px] p-8">
              To reduce seats below current plan
              <br />
              Contact{' '}
              <a
                href="mailto:support@wandb.com"
                className="underline hover:text-teal-500">
                support@wandb.com
              </a>
            </div>
          </Tailwind>
        </Tooltip>
      ) : (
        decrementButton
      )}
      <input />
      <S.NumberButton
        className="right icon mini attached"
        variant="secondary"
        size="large"
        onClick={handleIncrement}
        disabled={value >= max}>
        <IconAddNew />
      </S.NumberButton>
    </S.NumberInput>
  );
};

export const TeamsPlanDescription = ({plan}: {plan: PlanWithPrivileges}) => {
  const DESCRIPTION_LIST = [
    'Unlimited teams for collaboration',
    'Team-based access controls',
    'Slack & email alerts',
    `Up to ${plan.maxSeats} seats`,
    `Up to ${numeral(plan.defaultPrivileges.computeHours).format('0,00')} ${
      plan.billingInterval === 'year' ? 'annual' : 'monthly'
    } tracked hours`,
    `${plan.defaultPrivileges.storageLimitGB} GB free Storage included`,
    'Priority email & chat support',
  ];
  return (
    <S.DescriptionList>
      {DESCRIPTION_LIST.map((text, i) => (
        <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
      ))}
      <S.DescriptionListItem key="view pricing">
        <S.ViewPricingLink href={pricing()}>View pricing</S.ViewPricingLink>
      </S.DescriptionListItem>
    </S.DescriptionList>
  );
};

export const ProPlanDescription = ({
  primaryPlan,
}: {
  primaryPlan: PlanWithPrivileges;
}) => {
  const DESCRIPTION_LIST = [
    'Unlimited teams for collaboration',
    'Team-based access controls',
    'Slack & email alerts',
    `Up to 10 models seats`,
    `Up to ${numeral(
      (primaryPlan.defaultPrivileges.weaveLimits?.weaveLimitBytes ?? 0) /
        1_000_000
    ).format('0,00')} MB Weave data ingestion/mo`,
    `Up to ${numeral(
      primaryPlan.defaultPrivileges.computeHoursMonthlyLimit
    ).format('0,00')} tracked hours/mo`,
    `${primaryPlan.defaultPrivileges.storageLimitGB} GB free Storage included`,
    'Priority email & chat support',
  ];
  return (
    <S.DescriptionList>
      {DESCRIPTION_LIST.map((text, i) => (
        <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
      ))}
      <S.DescriptionListItem key="view pricing">
        <S.ViewPricingLink href={pricing()}>View pricing</S.ViewPricingLink>
      </S.DescriptionListItem>
    </S.DescriptionList>
  );
};
